/**
 * The client portal's "Safety register" (MB's R220, 4 Nov 2026; decisions 1023, 1035, 1037; help
 * `cloud.client-safety-register`; docs/specs/safety-register.md §10): shaped like the staff page
 * (`SafetyRegisterScreen`) with only the records the operator's tenant admins and managers approved for this client
 * organisation ("Approve for client"), of every project of the organisation: the totals of those records, the Project
 * and Kind filters, the table (date, project, kind, topic, state) and each record's details. Read only.
 *
 * Decision 1035: the details are the whole record, every field the staff details show (`registerDetailLines`: the
 * follow-up, people's roles, the landholder's contact, the place on the map, the linked photos, who recorded and closed
 * it), and the photos' display size and thumbnail open here. Decision 1037: no "Print the form" (an internal form).
 * A height breach has "Open breach report" (decision 1034, R219's `breach_report_pdf`): the project's latest Height
 * breach report PDF in a new tab, once a laptop has sent one (moved here from the project's Safety tab by decision
 * 1037). Preview as client shows exactly this for the organisation previewed.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  BREACH_RECORD_SOURCE_WORDS,
  BREACH_REPORT_OPEN_WORDS,
  REGISTER_KINDS,
  REGISTER_KIND_TOTAL_WORDS,
  REGISTER_KIND_WORDS,
  jspPrintedDate,
  registerDetailLines,
  registerRecordShown,
  safetyTotals,
  type ClientRegisterRecord,
  type RegisterKind,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import {
  breachReportUrl,
  loadBreachReportPdf,
  openInNewTab,
  type BreachReportPdf,
} from '../deliverables/breachReportApi.ts';
import { loadClientSafetyRegister, registerPhotoUrl, registerThumbUrls } from './client-register-api.ts';
import { StateChip } from './SafetyRegisterScreen.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const printed = (iso: string | null): string => (iso ? jspPrintedDate(iso.slice(0, 10)) : '');

export interface ClientSafetyRegisterScreenProps {
  readonly sb: SupabaseClient;
  /** The operator's organisation (the photos' objects are named by it). */
  readonly tenantId: string;
  readonly tenantName: string;
  readonly clientOrgId: string;
  readonly clientOrgName: string;
  /** A notice's link opens the page on its project (`?page=safety-register&project=<id>`). */
  readonly initialProject?: string | null;
  /** Today (YYYY-MM-DD) for an overdue follow-up; tests pass their own. */
  readonly today?: string;
  /** Open an address in a new tab (tests record it). */
  readonly open?: (url: string) => void;
  /** The project's latest breach report PDF, or null (`breach_report_pdf`); tests pass their own. */
  readonly findReport?: (projectId: string) => Promise<BreachReportPdf | null>;
}

export function ClientSafetyRegisterScreen({
  sb,
  tenantId,
  tenantName,
  clientOrgId,
  clientOrgName,
  initialProject = null,
  today = new Date().toISOString().slice(0, 10),
  open = openInNewTab,
  findReport,
}: ClientSafetyRegisterScreenProps): ReactElement {
  const [rows, setRows] = useState<ClientRegisterRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState(initialProject ?? 'all');
  const [kind, setKind] = useState<'all' | RegisterKind>('all');
  const [selected, setSelected] = useState<string | null>(null);

  const reload = (): void => {
    setError(null);
    loadClientSafetyRegister(sb, clientOrgId)
      .then(setRows)
      .catch((e: unknown) => setError(message(e)));
  };
  useEffect(reload, [sb, clientOrgId]);

  const projects = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows ?? []) m.set(r.project_id, `${r.project_number} ${r.project_title}`.trim());
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  // a notice's project with nothing approved now (withdrawn since) shows every project
  const projectShown = project === 'all' || projects.some(([id]) => id === project) ? project : 'all';
  const inProject = (rows ?? []).filter((r) => registerRecordShown(r, { project: projectShown }));
  const shown = inProject.filter((r) => registerRecordShown(r, { kind }));
  const totals = safetyTotals(inProject);
  const current = (rows ?? []).find((r) => `${r.source}:${r.id}` === selected) ?? null;

  return (
    <section className="page page--wide">
      <PageHeader
        title="Safety register"
        subtitle={`The accidents, incidents, near misses, landholder complaints and height breaches of ${clientOrgName}'s surveys that ${tenantName} has approved for you, read only.`}
      />
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="the safety register" />}
      {rows && (
        <div className="stack">
          <div className="safety-totals-box">
            <ul className="metric-tiles safety-totals" aria-label="Totals">
              {[
                ...REGISTER_KINDS.map((k) => [REGISTER_KIND_TOTAL_WORDS[k], totals[k]] as const),
                ['Open', totals.open] as const,
              ].map(([label, n]) => (
                <li key={label} className="metric-tile">
                  <span className="label">{label}</span>
                  <span className="metric metric-tile__value">{n}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="row row--wrap" role="group" aria-label="Filters">
            <label className="row">
              <span className="label">Project</span>
              <select value={projectShown} onChange={(e) => setProject(e.target.value)}>
                <option value="all">All projects</option>
                {projects.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="row">
              <span className="label">Kind</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as 'all' | RegisterKind)}>
                <option value="all">Every kind</option>
                {REGISTER_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {REGISTER_KIND_WORDS[k]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Date</th>
                  <th className="label">Project</th>
                  <th className="label">Kind</th>
                  <th className="label">Topic</th>
                  <th className="label">State</th>
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted">
                      {rows.length === 0
                        ? `Nothing from the safety register has been shared with you yet. ${tenantName} chooses which entries you see.`
                        : 'No entry matches these filters.'}
                    </td>
                  </tr>
                )}
                {shown.map((r) => {
                  const key = `${r.source}:${r.id}`;
                  return (
                    <tr key={key} aria-selected={key === selected}>
                      <td className="data">
                        {printed(r.occurred_on)}
                        {r.occurred_at && <div className="caption muted">{r.occurred_at}</div>}
                      </td>
                      <td className="body">
                        <span className="data">{r.project_number}</span> {r.project_title}
                      </td>
                      <td className="body">
                        {REGISTER_KIND_WORDS[r.kind]}
                        {r.from_breach && <div className="caption muted">{BREACH_RECORD_SOURCE_WORDS}</div>}
                      </td>
                      <td className="body">{r.topic}</td>
                      <td className="caption">
                        <StateChip record={r} today={today} />
                      </td>
                      <td className="table__actions">
                        <Button
                          variant="quiet"
                          aria-label={`Details of ${r.topic}`}
                          onClick={() => setSelected(key === selected ? null : key)}
                        >
                          {key === selected ? 'Hide details' : 'Details'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {current && (
            <Details
              key={`${current.source}:${current.id}`}
              sb={sb}
              tenantId={tenantId}
              entry={current}
              open={open}
              findReport={findReport ?? ((id) => loadBreachReportPdf(sb, id).catch(() => null))}
            />
          )}
        </div>
      )}
    </section>
  );
}

/** A record's details: every field (decision 1035), its photos, and a breach's report PDF. */
function Details({
  sb,
  tenantId,
  entry: e,
  open,
  findReport,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly entry: ClientRegisterRecord;
  readonly open: (url: string) => void;
  readonly findReport: (projectId: string) => Promise<BreachReportPdf | null>;
}): ReactElement {
  const [report, setReport] = useState<BreachReportPdf | null>(null);
  const [thumbs, setThumbs] = useState<ReadonlyMap<string, string>>(new Map());
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const live = e.photos.filter((p) => !p.deleted_at);

  useEffect(() => {
    let on = true;
    if (e.from_breach) void findReport(e.project_id).then((r) => on && setReport(r));
    if (live.length > 0)
      void registerThumbUrls(
        sb,
        tenantId,
        e.project_id,
        live.map((p) => p.id),
      ).then((m) => on && setThumbs(m));
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per record (the component is keyed by it)
  }, []);

  async function press(what: string, url: () => Promise<string>): Promise<void> {
    setBusy(what);
    setProblem(null);
    try {
      open(await url());
    } catch (err) {
      setProblem(message(err));
    } finally {
      setBusy(null);
    }
  }

  const rows: [string, string][] = [
    ...registerDetailLines(e, e.photos),
    ['Shared with you', printed(e.shown_at)],
  ];
  return (
    <div className="panel stack" role="region" aria-label={`Entry: ${e.topic}`}>
      <h2 className="heading">{e.topic}</h2>
      {e.from_breach && (
        <p className="notice" role="note">
          {BREACH_RECORD_SOURCE_WORDS}.{' '}
          {report && (
            <Button
              variant="quiet"
              busy={busy === 'report'}
              onClick={() => void press('report', () => breachReportUrl(sb, report))}
            >
              {BREACH_REPORT_OPEN_WORDS}
            </Button>
          )}
          {report && (
            <span className="caption muted">The project’s height breach report, every breach in it.</span>
          )}
        </p>
      )}
      {problem && (
        <p className="notice notice--fail" role="alert">
          {problem}
        </p>
      )}
      <dl className="kv safety-details">
        {rows.map(([k, v]) => (
          <div key={k} className="safety-details__row">
            <dt className="label">{k}</dt>
            <dd className="body safety-details__text">{v}</dd>
          </div>
        ))}
      </dl>
      {live.length > 0 && (
        <ul className="photo-grid" aria-label="Photos">
          {live.map((p) => {
            const title = p.caption.trim() || p.file_name;
            const thumb = thumbs.get(p.id);
            return (
              <li key={p.id} className="photo-tile">
                <button
                  type="button"
                  className="photo-tile__open"
                  aria-label={`Open ${title}`}
                  disabled={busy === p.id}
                  onClick={() => void press(p.id, () => registerPhotoUrl(sb, tenantId, e.project_id, p.id))}
                >
                  {thumb ? (
                    <img className="photo-tile__img" src={thumb} alt="" loading="lazy" />
                  ) : (
                    <span className="photo-tile__none caption muted">{p.file_name}</span>
                  )}
                </button>
                <div className="photo-tile__body">
                  <p className="body photo-tile__title">{title}</p>
                  {title !== p.file_name && <p className="caption muted data">{p.file_name}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
